import { Link } from 'react-router-dom';
import {
  makeStyles, shorthands, tokens, Text, Badge, ProgressBar,
  MessageBar, MessageBarBody, Tooltip,
} from '@fluentui/react-components';
import { Info16Regular } from '@fluentui/react-icons';
import { Page, PageHeader, Card, Loading, ErrorState, Empty, SeverityBadge } from '../components/common';
import { useCustomer } from '../context/CustomerContext';
import { useApi } from '../api/context';
import { useAsync } from '../api/useAsync';
import { scorePosture, pillarPosture, topRisks, pillarScores } from '../theme/posture';
import { RadarChart } from '../components/RadarChart';
import { SEVERITY_ORDER, severityTokens, pillarAccent, pillarWash } from '../theme/pillars';

const useStyles = makeStyles({
  hero: { display: 'grid', gridTemplateColumns: 'minmax(200px, 240px) 1fr minmax(320px, 360px)', columnGap: tokens.spacingHorizontalXXL, rowGap: tokens.spacingVerticalL, alignItems: 'start' },
  scoreBox: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalXS, alignItems: 'flex-start' },
  score: { fontSize: '64px', fontWeight: tokens.fontWeightSemibold, lineHeight: '1' },
  label: {
    color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200,
    textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: tokens.fontWeightSemibold,
  },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  sevRow: { display: 'flex', flexWrap: 'wrap', columnGap: tokens.spacingHorizontalL, rowGap: tokens.spacingVerticalM },
  sevItem: { display: 'flex', flexDirection: 'column', rowGap: '2px', minWidth: '84px' },
  sevCount: { fontSize: tokens.fontSizeHero700, fontWeight: tokens.fontWeightSemibold, lineHeight: '1' },
  heat: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', columnGap: tokens.spacingHorizontalM, rowGap: tokens.spacingVerticalM },
  tile: {
    display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalXS,
    textDecorationLine: 'none', color: 'inherit', position: 'relative',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM, tokens.spacingVerticalM, tokens.spacingHorizontalL),
    ...shorthands.overflow('hidden'),
    ':hover': { boxShadow: tokens.shadow8 },
  },
  // The pillar's own colour, so the reader learns it once and recognises it in
  // the radar, the tiles and the pillar pages.
  tileAccent: { position: 'absolute', left: 0, top: 0, bottom: 0, width: '5px' },
  tileTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  risk: {
    display: 'flex', alignItems: 'flex-start', columnGap: tokens.spacingHorizontalS,
    ...shorthands.padding(tokens.spacingVerticalS, 0),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke3),
  },
  section: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalM },
});

export function Overview() {
  const styles = useStyles();
  const { tenant, run, runs, findings, primitives, pillarOf, loading, error } = useCustomer();
  const api = useApi();

  // Every hook must be called before any early return below. React tracks
  // hooks by call order, so a hook that runs only on some renders makes the
  // count differ between them, and React responds by throwing and unmounting
  // the tree. That shows as a blank page with nothing on screen to explain it.
  const previousRun = runs.find((r) => r.status === 'complete' && r.runId !== run?.runId);
  const previousFindings = useAsync(
    () => (previousRun ? api.getFindings(previousRun.runId) : Promise.resolve(null)),
    [previousRun?.runId],
  );

  if (loading) return <Loading />;
  if (error) return <ErrorState error={error} />;

  if (!tenant) {
    return (
      <Page>
        <PageHeader title="Overview" />
        <Empty>Select a customer to see their security posture.</Empty>
      </Page>
    );
  }

  if (!run) {
    return (
      <Page>
        <PageHeader title={tenant.displayName} />
        <Empty>No collection has run for this customer yet.</Empty>
      </Page>
    );
  }

  const measurableByPillar = primitives.reduce<Record<string, number>>((acc, p) => {
    acc[p.msPillar] = (acc[p.msPillar] ?? 0) + 1;
    return acc;
  }, {});

  const posture = scorePosture(findings, primitives.length);
  const pillars = pillarPosture(findings, pillarOf, measurableByPillar);
  const risks = topRisks(findings);
  const previous = previousRun;
  const delta = previous ? findings.length - previous.findingCount : null;

  const current = pillarScores(pillars);

  // The previous ring is computed from that run's actual findings, fetched for
  // the purpose. An earlier version spread its finding count evenly across the
  // pillars, which drew a shape that had never been measured. A dashed line on
  // invented data is still invented data.
  const previousPillars = previousFindings.data
    ? pillarPosture(previousFindings.data, pillarOf, measurableByPillar)
    : null;

  const radarSeries = [
    { label: 'This collection', colour: '#0F6CBD', values: current },
    ...(previousPillars
      ? [{ label: 'Previous collection', colour: '#8A8886', dashed: true,
           values: pillarScores(previousPillars) }]
      : []),
  ];

  const bandColour =
    posture.band === 'Strong' ? tokens.colorStatusSuccessForeground1
      : posture.band === 'Reasonable' ? tokens.colorBrandForeground1
      : posture.band === 'Weak' ? tokens.colorStatusWarningForeground1
      : tokens.colorStatusDangerForeground1;

  return (
    <Page>
      <PageHeader
        title={tenant.displayName}
        description={`Collection of ${new Date(run.startedUtc).toISOString().slice(0, 16).replace('T', ' ')} · ${tenant.homeRegion}`}
      />

      {run.status === 'failed' && (
        <MessageBar intent="error">
          <MessageBarBody>
            This collection failed and is incomplete. The score below is calculated from
            partial data and should not be presented to the customer.
          </MessageBarBody>
        </MessageBar>
      )}

      <Card>
        <div className={styles.hero}>
          <div className={styles.scoreBox}>
            <span className={styles.label}>Posture score</span>
            <span className={styles.score} style={{ color: bandColour }}>{posture.score}</span>
            <Badge appearance="tint" color={posture.band === 'Strong' ? 'success' : posture.band === 'Poor' ? 'danger' : 'warning'}>
              {posture.band}
            </Badge>
            <ProgressBar value={posture.score / 100} thickness="large" />

            {/* A number on a dashboard invites more trust than it deserves, so
                the basis travels with it rather than living in a manual. */}
            <Tooltip content={posture.basis} relationship="description" withArrow>
              <span className={styles.muted}>
                <Info16Regular /> How this is calculated
              </span>
            </Tooltip>

            {delta !== null && (
              <Text className={styles.muted}>
                {delta === 0 ? 'No change from the previous collection.'
                  : delta > 0 ? `${delta} more finding(s) than the previous collection.`
                  : `${Math.abs(delta)} fewer finding(s) than the previous collection.`}
              </Text>
            )}
          </div>

          <div className={styles.section}>
            <div className={styles.sevRow}>
              {SEVERITY_ORDER.map((s) => {
                const n = findings.filter((f) => f.severity === s).length;
                const { foreground } = severityTokens(s);
                return (
                  <div key={s} className={styles.sevItem}>
                    <span className={styles.sevCount} style={{ color: n > 0 ? foreground : tokens.colorNeutralForeground4 }}>
                      {n}
                    </span>
                    <span className={styles.label}>{s}</span>
                  </div>
                );
              })}
            </div>

            <Text className={styles.muted}>
              {posture.affected} of {posture.measurable} measurable control outcomes raised a
              finding. A control with no finding is not evidence of compliance: it means
              nothing was raised, which may be a pass or a check that did not run.
            </Text>
          </div>

          <RadarChart
            axes={pillars.map((p) => p.label)}
            series={radarSeries}
            caption={
              previous
                ? 'Solid is this collection, dashed is the previous one, both computed from their own findings. There is no industry average line: we hold no industry data, and an invented comparison would be worse than none.'
                : 'One collection so far. A second will be drawn against it for comparison. There is no industry average line, because we hold no industry data.'
            }
          />
        </div>
      </Card>

      <Card>
        <div className={styles.section}>
          <Text weight="semibold" size={400}>By Zero Trust pillar</Text>
          <div className={styles.heat}>
            {pillars.map((p) => (
              <Link key={p.pillar} to={p.route} className={styles.tile}
                    style={{ backgroundColor: pillarWash(p.pillar) }}>
                <span className={styles.tileAccent}
                      style={{ backgroundColor: pillarAccent(p.pillar) }} />
                <div className={styles.tileTop}>
                  <Text weight="semibold">{p.label}</Text>
                  {p.worst ? <SeverityBadge severity={p.worst} /> : (
                    <Badge appearance="outline" size="small">Clear</Badge>
                  )}
                </div>
                <Text size={600} weight="semibold">{p.total}</Text>
                <span className={styles.muted}>
                  {p.affected} of {p.measurable} outcomes affected
                </span>
              </Link>
            ))}
          </div>
        </div>
      </Card>

      <Card>
        <div className={styles.section}>
          <Text weight="semibold" size={400}>Raise these first</Text>
          <Text className={styles.muted}>
            Critical and high findings, ordered by severity then by how many entities each
            one affects.
          </Text>
          {risks.length === 0 ? (
            <Empty>No critical or high findings in this collection.</Empty>
          ) : (
            risks.map((f) => (
              <div key={f.findingId} className={styles.risk}>
                <SeverityBadge severity={f.severity} />
                <span style={{ flexGrow: 1 }}>
                  <Text weight="semibold">{f.title}</Text>
                  <div className={styles.muted}>{f.checkId} · {f.area}</div>
                </span>
                {f.resources.length > 0 && (
                  <Badge appearance="tint" color="informative" size="small">
                    {f.resources.length} affected
                  </Badge>
                )}
              </div>
            ))
          )}
        </div>
      </Card>
    </Page>
  );
}
